import { faBoxOpen, faSpinner } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import moment from 'moment';
import React, { useEffect, useState } from 'react';
import fleetApi from '../../services/api.service';
import GenericModal from '../../components/agv-generic-modal';
import InputText from '../../components/agv-forms-input';

/**
 * Modal d'export de l'état des palettes consignées pour une date donnée.
 * Seuls les véhicules ayant une tournée finalisée ce jour-là sont inclus.
 */
export default function ExportVehiclePackagingModal({ isOpen, setIsOpen }) {
  const [date, setDate] = useState(moment().format('YYYY-MM-DD'));
  const [vehicleKeys, setVehicleKeys] = useState([]);
  const [isLoadingVehicles, setIsLoadingVehicles] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [error, setError] = useState(null);

  // Recharger la liste des véhicules finalisés à chaque ouverture ou changement de date.
  useEffect(() => {
    if (!isOpen) return;

    const controller = new AbortController();
    let isActive = true;

    const loadVehicles = async () => {
      setIsLoadingVehicles(true);
      setVehicleKeys([]);
      setError(null);
      try {
        const response = await fleetApi.get(
          `/consignment/vehicles-with-finalized-tours?date=${date}`,
          { signal: controller.signal }
        );
        if (!isActive) return;
        setVehicleKeys(response.data?.vehicle_keys ?? []);
      } catch (err) {
        if (controller.signal.aborted || !isActive) return;
        setError('Impossible de charger les véhicules pour cette date.');
      } finally {
        if (isActive) setIsLoadingVehicles(false);
      }
    };

    loadVehicles();

    return () => {
      isActive = false;
      controller.abort();
    };
  }, [isOpen, date]);

  const handleExport = async () => {
    setIsExporting(true);
    setError(null);
    try {
      const response = await fleetApi.get(
        `/consignment/export/vehicle-packaging?date=${date}`,
        { responseType: 'blob' }
      );

      if (response.data.size === 0) {
        throw new Error('Aucune tournée finalisée trouvée pour cette date.');
      }

      const url = window.URL.createObjectURL(response.data);
      const a = document.createElement('a');
      a.href = url;
      a.download = `etat-palettes-${date}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);

      setIsOpen(false);
    } catch (err) {
      const msg =
        err?.response?.data instanceof Blob
          ? await err.response.data.text().then((t) => {
              try {
                return JSON.parse(t)?.message;
              } catch {
                return t;
              }
            })
          : err.message;
      setError(msg || "Erreur lors de l'export.");
    } finally {
      setIsExporting(false);
    }
  };

  const handleClose = () => {
    if (!isExporting) setIsOpen(false);
  };

  return (
    <GenericModal
      isOpen={isOpen}
      setIsOpen={handleClose}
      title="Export état des palettes"
      mainAction={handleExport}
      mainActionText="Exporter en XLSX"
      mainActionIsLoading={isExporting}
      mainActionDisabled={isLoadingVehicles || vehicleKeys.length === 0}
      secondaryAction={handleClose}
      secondaryActionText="Annuler"
    >
      <div className="tw-flex tw-flex-col tw-gap-4">
        {error && (
          <div className="tw-text-sm tw-text-input-required tw-bg-red-50 tw-border tw-border-red-200 tw-rounded-lg tw-px-3 tw-py-2">
            {error}
          </div>
        )}

        <InputText
          value={date}
          setValue={setDate}
          fieldTitle="Date"
          type="date"
          disabled={isExporting}
        />

        <div>
          <div className="tw-flex tw-justify-between tw-text-text-dark-medium-importance tw-items-center tw-mb-3 tw-text-sm">
            <div className="tw-text-s">Véhicules avec tournée finalisée</div>
          </div>

          {isLoadingVehicles ? (
            <div className="tw-flex tw-items-center tw-gap-2 tw-text-sm tw-text-text-dark-medium-importance">
              <FontAwesomeIcon icon={faSpinner} spin />
              Chargement…
            </div>
          ) : vehicleKeys.length === 0 ? (
            <p className="tw-text-sm tw-text-text-dark-medium-importance tw-italic">
              Aucune tournée finalisée pour cette date.
            </p>
          ) : (
            <div className="tw-flex tw-flex-wrap tw-gap-2">
              {vehicleKeys.map((key) => (
                <span
                  key={key}
                  className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-background tw-text-text-dark-high-importance tw-text-xs tw-font-medium tw-px-2.5 tw-py-1 tw-rounded-full"
                >
                  <FontAwesomeIcon
                    icon={faBoxOpen}
                    className="tw-text-checklist-common tw-text-[10px]"
                  />
                  {key}
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </GenericModal>
  );
}
